import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { hasCallToAction } from '../utils';

import Block from './Block';
import H2 from './H2';
import CTAAnchor from './CTAAnchor';
import Grid from './Grid';
import Cell from './Cell';
import Image from './Image';
import RichText from './RichText';

const ImageOverlay = styled(Block)`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: linear-gradient(
    180deg,
    hsla(0, 0%, 35.29%, 0) 0%,
    hsla(0, 0%, 34.53%, 0.034375) 16.36%,
    hsla(0, 0%, 32.42%, 0.125) 33.34%,
    hsla(0, 0%, 29.18%, 0.253125) 50.1%,
    hsla(0, 0%, 24.96%, 0.4) 65.75%,
    hsla(0, 0%, 19.85%, 0.546875) 79.43%,
    hsla(0, 0%, 13.95%, 0.675) 90.28%,
    hsla(0, 0%, 7.32%, 0.765625) 97.43%,
    hsla(0, 0%, 0%, 0.8) 100%
  );
`;

const ImageGrid = styled(Block)`
  max-width: 1440px;
  margin: 0 auto;

  ${breakpoint('smallTablet')`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: ${({ theme }) => theme.spacing.baseUnit};
    column-gap: ${({ theme }) => theme.spacing.baseUnit};
    grid-row-gap: ${({ theme }) => theme.spacing.baseUnit};
    row-gap: ${({ theme }) => theme.spacing.baseUnit};
  `};

  ${breakpoint('tablet')`
    grid-template-columns: ${({ numberOfColumns }) =>
      `repeat(${numberOfColumns}, 1fr)`};
  `}
`;

const ImageGridItem = styled(Block)`
  ${breakpoint('tablet')`
    grid-column-start: ${({ gridColumnStart }) => gridColumnStart || 0};
  `}
`;

const PageImageGrid = ({ content }) => (
  <Block
    id={content.primary.internal_link_id}
    py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
    px="baseUnit"
  >
    <Grid>
      <Cell size={1} position="relative">
        {content.primary.title1 && content.primary.title1.text && (
          <H2 px="baseUnit" mb="baseUp4">
            {content.primary.title1.text}
          </H2>
        )}
      </Cell>
    </Grid>

    <ImageGrid numberOfColumns={content.primary.number_of_columns}>
      {content.items.map((item, i) => (
        <ImageGridItem
          key={`page-image-item-${i}`}
          my={{ mobile: 'baseUnit', smallTablet: '0' }}
          position="relative"
          gridColumnStart={item.grid_column_start}
        >
          <Image content={item.image} />

          <ImageOverlay
            position="absolute"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-end"
            p="baseUp4"
          >
            <RichText
              content={item.text.raw}
              options={{
                paragraph: {
                  color: 'white',
                  textAlign: 'center',
                  mb: 'baseDown2',
                },
                heading3: {
                  color: 'white',
                  textAlign: 'center',
                  mb: 'baseDown2',
                },
                heading4: {
                  color: 'white',
                  textAlign: 'center',
                  mb: 'baseDown2',
                },
                heading5: {
                  color: 'white',
                  textAlign: 'center',
                  mb: 'baseDown2',
                },
              }}
            />

            {hasCallToAction(item) ? (
              <CTAAnchor
                text={item.call_to_action_text.text}
                link={item.call_to_action_link}
                colorTheme={item.call_to_action_color}
              />
            ) : null}
          </ImageOverlay>
        </ImageGridItem>
      ))}
    </ImageGrid>
  </Block>
);

PageImageGrid.propTypes = {
  content: PropTypes.shape({
    primary: PropTypes.shape({
      internal_link_id: PropTypes.string,
      title1: PropTypes.shape({
        text: PropTypes.string.isRequired,
      }).isRequired,
      number_of_columns: PropTypes.string.isRequired,
    }).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
};

export default PageImageGrid;

export const query = graphql`
  fragment PageImageGridFragment on PrismicPageDataBodyImageGrid {
    id
    slice_type
    primary {
      internal_link_id
      title1 {
        text
      }
      number_of_columns
    }
    items {
      grid_column_start
      text {
        raw
      }
      image {
        alt
        localFile {
          url
          extension
          childImageSharp {
            gatsbyImageData(width: 720)
          }
        }
      }
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
    }
  }
`;
